import { useState, useRef } from 'react';

import loadGoogleAndFacebookScripts from 'Jsx/Auth/loadGoogleAndFacebookScripts';
import { Me } from 'Jsx/ContextApis/AuthContext';

// tracking
import { sendRegisterTrackToGoogleAdsAndAnalytics } from 'Jsx/Auth/utils';

// utils
import getFacebookAuthLinkFeatureFlags from 'Utils/getFacebookAuthLinkFeatureFlags';
import getNextUrl from 'Jsx/Utils/getNextUrl';
import customLocalStorage from 'Utils/customLocalStorage';
import createApolloClient from 'Shared/LayoutV2/ApolloClient';

// constants
import EVENTS from 'Jsx/Auth/constants/Tracking';

import SOCIAL_LOGIN_MUTATION from '../../graphql/socialLogin.graphql';

export const authGraphqlClient = createApolloClient({
  uri: '/api/v2/graph/auth/',
});

export type UseSocialAuthOptions = {
  referFriendId?: number;
  nextUrl?: string;
  onSuccess?: (params: {
    redirect: string;
    user?: Me;
    isNewUser?: boolean;
  }) => void;
  onFinishToLoadScripts?: (success: boolean) => void;
  /**
   * Pass the page, section or component where this component is used.
   * This prop is used to send extra data to the mixpanel track "AUTH_REGISTER"
   * Ex: If you pass "catalog" the mixpanel track is sent as: `mixpanel(REGISTER, { isRenderedIn })`
   */
  isRenderedIn?: string;
  getNextUrl?: () => string | undefined;
};

export const googleFallbackUrl = '/login/google-oauth2/';
export const facebookFallbackUrl = '/login/facebook/';

let idCounter = 0;
const genId = () => {
  idCounter += 1;
  return idCounter;
};

export function useSocialAuth({
  referFriendId,
  nextUrl: nextUrlProp,
  onSuccess,
  onFinishToLoadScripts,
  isRenderedIn,
  getNextUrl: getNextUrlProp,
}: UseSocialAuthOptions = {}) {
  const gmailButtonRef = useRef<HTMLButtonElement>(null);
  const gmailHTMLButtonId = useRef(`customGoogleButton-${genId()}`);
  const [processingSocialAuth, setProcessingSocialAuth] = useState(false);

  const [state, setState] = useState({
    loadingFacebookAndGoogleScripts: false,
    errorToLoadFacebookAndGoogleScripts: false,
  });

  const _getNextUrl = () => {
    if (getNextUrlProp) {
      return getNextUrlProp();
    }

    return nextUrlProp || getNextUrl();
  };

  const nextUrl = _getNextUrl();

  const login = variables => {
    return authGraphqlClient.mutate({
      mutation: SOCIAL_LOGIN_MUTATION,
      variables: {
        token: variables.token,
        nextUrl: variables.nextUrl,
        backend: variables.backend,
        referFriendId,
      },
    });
  };

  const redirectToFacebookLoginPage = () => {
    window.location.href = `${facebookFallbackUrl}?next=${nextUrl}`;
  };

  const redirectToGoogleLoginPage = () => {
    window.location.href = `${googleFallbackUrl}?next=${nextUrl}`;
  };

  const attachSignin = element => {
    if (!element) {
      return;
    }

    window.auth2.attachClickHandler(
      element,
      {},
      googleUser => {
        setProcessingSocialAuth(true);
        login({
          token: googleUser.getAuthResponse(true).access_token,
          nextUrl,
          backend: 'google-oauth2',
        }).then(response => {
          setProcessingSocialAuth(false);
          if (response.data.socialLogin.success) {
            const { redirect, isNewUser } = response.data.socialLogin;

            if (isNewUser) {
              sendRegisterTrackToGoogleAdsAndAnalytics();
              window.track(EVENTS.REGISTER, {
                provider: 'gmail',
                isRenderedIn,
              });
            }
            if (onSuccess) {
              onSuccess({ redirect, isNewUser });
            } else {
              setTimeout(() => {
                location.href = redirect || '/';
              }, 0);
            }
          } else {
            redirectToGoogleLoginPage();
          }
        });
      },
      error => {
        console.log('Google error auth', error);
        redirectToGoogleLoginPage();
      },
    );
  };

  const initializeGoogle = () => {
    window.gapi.load('auth2', () => {
      // Retrieve the singleton for the GoogleAuth library and set up the client.
      window.auth2 = window.gapi.auth2.init({
        client_id: window.GLOBAL_GOOGLE_CLIENT_ID,
        cookiepolicy: 'single_host_origin',
        // Request scopes in addition to 'profile' and 'email'
        // scope: 'additional_scope'
      });
      attachSignin(gmailButtonRef.current);
    });
  };

  const loginWithFacebook = () => {
    const facebookLinkFeatureFlags = getFacebookAuthLinkFeatureFlags();

    // RemoveConditionThatPreventsContinueWithFacebookFloatingButtonToBeShown
    customLocalStorage.removeItem('CREH__continueAsFacebookUserDialog');

    if (facebookLinkFeatureFlags.facebookAuthLink) {
      redirectToFacebookLoginPage();
      return;
    }

    if (typeof window !== 'undefined' && window.FB) {
      window.FB.getLoginStatus(res => {
        const backend = 'facebook';

        if (res.status === 'connected') {
          const token = res.authResponse.accessToken;

          setProcessingSocialAuth(true);

          login({
            token,
            nextUrl,
            backend,
          }).then(response => {
            setProcessingSocialAuth(false);
            if (response.data.socialLogin.success) {
              const { redirect } = response.data.socialLogin;

              if (onSuccess) {
                onSuccess({ redirect });
              } else {
                location.href = redirect || '/';
              }
            } else {
              redirectToFacebookLoginPage();
            }
          });
        } else {
          window.FB.login(
            loginResponse => {
              if (loginResponse.authResponse) {
                const token = loginResponse.authResponse.accessToken;

                setProcessingSocialAuth(true);
                login({
                  token,
                  nextUrl,
                  backend,
                }).then(response => {
                  setProcessingSocialAuth(false);
                  if (response.data.socialLogin.success) {
                    const { redirect, isNewUser } = response.data.socialLogin;

                    if (isNewUser) {
                      sendRegisterTrackToGoogleAdsAndAnalytics();
                      window.track(EVENTS.REGISTER, {
                        provider: 'facebook',
                        isRenderedIn,
                      });
                    }
                    if (onSuccess) {
                      onSuccess({ redirect, isNewUser });
                    } else {
                      setTimeout(() => {
                        location.href = redirect || '/';
                      }, 10);
                    }
                  } else {
                    redirectToFacebookLoginPage();
                  }
                });
              } else {
                redirectToFacebookLoginPage();
              }
            },
            { scope: 'email' },
          );
        }
      });
    }
  };

  function setupAndInitSocialAuthScripts() {
    setState({
      ...state,
      loadingFacebookAndGoogleScripts: true,
    });
    loadGoogleAndFacebookScripts()
      .then(() => {
        if (onFinishToLoadScripts) {
          onFinishToLoadScripts(true);
        }

        setState(prevState => ({
          ...prevState,
          errorToLoadFacebookAndGoogleScripts: false,
          loadingFacebookAndGoogleScripts: false,
        }));
        initializeGoogle();
      })
      .catch(error => {
        console.error('Errot to load the facebook and google skd', error);
        if (onFinishToLoadScripts) {
          onFinishToLoadScripts(false);
        }
        setState(prevState => ({
          ...prevState,
          errorToLoadFacebookAndGoogleScripts: true,
          loadingFacebookAndGoogleScripts: false,
        }));
      });
  }

  return {
    setupAndInitSocialAuthScripts,
    authState: {
      ...state,
      processingSocialAuth,
    },
    gmailButtonProps: {
      id: gmailHTMLButtonId.current,
      ref: gmailButtonRef,
    },
    facebookButtonProps: {
      onClick: loginWithFacebook,
    },
  };
}
